import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { Permission } from '@lumina/types';
import { ForbiddenPage } from '@/app/system-pages';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth, usePermissions } from './AuthProvider';

export function FullScreenLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-canvas">
      <div className="flex flex-col items-center gap-3 text-ink-muted">
        <Spinner size="lg" className="text-sapphire-600" label={label} />
        <p className="text-sm">{label}</p>
      </div>
    </div>
  );
}

interface GuardProps {
  children?: ReactNode;
  /** Where anonymous visitors are sent (the current URL is passed along as `state.from`). */
  redirectTo?: string;
}

/** Any signed-in user (patron or staff). Use as a layout route (`<Outlet/>`) or wrapping children. */
export function RequireAuth({ children, redirectTo = '/login' }: GuardProps) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') {
    return <FullScreenLoader />;
  }
  if (status === 'anonymous') {
    return <Navigate to={redirectTo} replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <>{children ?? <Outlet />}</>;
}

/** Staff console gate: anonymous -> /staff/login; a signed-in patron sees the Forbidden page. */
export function RequireStaff({ children, redirectTo = '/staff/login' }: GuardProps) {
  const { status, isStaff } = useAuth();
  const location = useLocation();
  if (status === 'loading') {
    return <FullScreenLoader />;
  }
  if (status === 'anonymous') {
    return <Navigate to={redirectTo} replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  if (!isStaff) {
    return <ForbiddenPage />;
  }
  return <>{children ?? <Outlet />}</>;
}

interface RequirePermissionProps {
  /** One permission, or several. */
  permission: Permission | readonly Permission[];
  /** With several permissions: 'any' (default) or 'all' must be held. */
  mode?: 'any' | 'all';
  children?: ReactNode;
  /** Rendered instead of the Forbidden page when access is denied. */
  fallback?: ReactNode;
}

/** Hides a section or a whole route behind a staff permission. */
export function RequirePermission({ permission, mode = 'any', children, fallback }: RequirePermissionProps) {
  const perms = usePermissions();
  const list = (Array.isArray(permission) ? permission : [permission]) as readonly Permission[];
  const allowed = mode === 'all' ? perms.hasAll(list) : perms.hasAny(list);
  if (!allowed) {
    return <>{fallback ?? <ForbiddenPage />}</>;
  }
  return <>{children ?? <Outlet />}</>;
}
